import React, { useContext } from 'react';
import styled from 'styled-components';
import { AppContext } from 'shared/AppContext';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const Card = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-size: 28px;
  }
`;

const CardText = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#475569')};
  line-height: 1.7;
  margin: 0 0 16px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 32px;
`;

const FeatureCard = styled(Card)`
  padding: 24px;
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const FeatureText = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.6;
  margin: 0;
`;

const ThemeToggle = styled.button`
  padding: 12px 24px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(250, 70, 22, 0.3)' : '#FA4616'};
  border-radius: 8px;
  color: #FA4616;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.12)'};
    transform: translateY(-1px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#0F172A' : '#F8FAFC'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin: 16px 0;
`;

const Overview = () => {
  const context = useContext(AppContext);
  const { state, dispatch } = context || { state: {}, dispatch: () => {} };

  const toggleTheme = () => {
    const newTheme = state.theme === 'dark' ? 'light' : 'dark';
    dispatch({ type: 'TOGGLE_THEME', payload: newTheme });
  };

  return (
    <div>
      <PageHeader>
        <Title>Welcome to the PitPay Design System</Title>
        <Subtitle>
          A comprehensive component library and style guide for building
          consistent, accessible, and beautiful interfaces across all PitPay
          admin applications.
        </Subtitle>
        <ThemeToggle onClick={toggleTheme}>
          {state.theme === 'dark' ? (
            <>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              Switch to Light Mode
            </>
          ) : (
            <>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
              Switch to Dark Mode
            </>
          )}
        </ThemeToggle>
      </PageHeader>

      <Card>
        <CardTitle>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <span>🎯</span>
          What is this?
        </CardTitle>
        <CardText>
          This style guide documents the PitPay Admin design system - a
          collection of reusable components, design tokens, and patterns built
          with React and styled-components. It serves as both documentation and
          a playground for exploring our UI components.
        </CardText>
        <CardText>
          The design system is based on the <strong>Customers</strong> and{' '}
          <strong>Transaction Detail</strong> pages, which showcase our best
          practices for theming, layout, and component composition.
        </CardText>
      </Card>

      <Card>
        <CardTitle>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <span>🚀</span>
          Quick Start
        </CardTitle>
        <CardText>
          Import components from the shared components directory:
        </CardText>
        <CodeBlock>
{`import Card from 'components/Card';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';

// Wrap your component with theme provider
<EnhancedThemeProvider>
  <Card title="My Card" accentTitle>
    <ButtonV2 variant="pill" active>
      Click me
    </ButtonV2>
  </Card>
</EnhancedThemeProvider>`}
        </CodeBlock>
      </Card>

      <Grid>
        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>🎨</FeatureIcon>
          <FeatureTitle>Design Tokens</FeatureTitle>
          <FeatureText>
            Explore our color palette, typography scale, spacing system, and
            shadow styles. All tokens work seamlessly in light and dark modes.
          </FeatureText>
        </FeatureCard>

        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>🧩</FeatureIcon>
          <FeatureTitle>Components</FeatureTitle>
          <FeatureText>
            Fully documented React components with PropTypes, examples, and
            usage guidelines. Copy-paste ready code snippets included.
          </FeatureText>
        </FeatureCard>

        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>🌓</FeatureIcon>
          <FeatureTitle>Theme System</FeatureTitle>
          <FeatureText>
            Built-in light and dark mode support using styled-components
            ThemeProvider. All components adapt automatically to theme changes.
          </FeatureText>
        </FeatureCard>

        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>🗺️</FeatureIcon>
          <FeatureTitle>Context-Aware Routing</FeatureTitle>
          <FeatureText>
            Components that work across three admin contexts: Regular Admin,
            Track Admin, and Employee Admin with proper route prefixes.
          </FeatureText>
        </FeatureCard>

        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>📱</FeatureIcon>
          <FeatureTitle>Responsive Design</FeatureTitle>
          <FeatureText>
            Mobile-first components with breakpoints at 768px and 1024px.
            Touch-friendly interactions on mobile devices.
          </FeatureText>
        </FeatureCard>

        <FeatureCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <FeatureIcon>♿</FeatureIcon>
          <FeatureTitle>Accessibility</FeatureTitle>
          <FeatureText>
            Components follow WCAG guidelines with proper ARIA labels, keyboard
            navigation, and focus management.
          </FeatureText>
        </FeatureCard>
      </Grid>

      <Card style={{ marginTop: '32px' }}>
        <CardTitle>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <span>📚</span>
          How to Use This Guide
        </CardTitle>
        <CardText>
          <strong>Design Tokens:</strong> Learn about our foundational design
          decisions - colors, typography, spacing, and shadows.
        </CardText>
        <CardText>
          <strong>Foundation:</strong> Understand layout primitives like Cards
          and Grid systems.
        </CardText>
        <CardText>
          <strong>Components:</strong> Browse individual components with live
          examples, prop documentation, and code snippets.
        </CardText>
        <CardText>
          <strong>Patterns:</strong> Discover common UI patterns like Metrics
          dashboards, Timelines, and Tables.
        </CardText>
      </Card>
    </div>
  );
};

export default Overview;